import { GenericAbortSignal } from 'axios';

import Upload from 'hooks/useDirectUpload/Upload';
import { JobProcessStatus } from 'models/JobProcess';
import { ReviewType } from 'models/Review';
import ReviewModelFactory from 'models/reviews/ReviewModelFactory';
import http from 'services/http';
import { type DateRange } from 'stores/admissionsStore';

import { indexReviews, ReviewsIndexParams } from './reviews';

export type AdmissionsIndexParams = ReviewsIndexParams & {
  dateRange?: DateRange | null;
};

export const admissionQueryKeys = {
  base: ['admissions'],
  index: (params: Record<string, any> = {}) => ['admissions', 'index', params],
  show: (params: Record<string, any> = {}) => ['admissions', 'show', params],
};

export const admissionQueryParams = {
  index: {
    include: [
      'createdBy',
      'group',
      'summaryJobForCurrentAttachments',
      'criteriaEvaluationJob',
      'patientDemographicsJob',
    ].join(','),
    'criteriaEvaluationStatus.not': JobProcessStatus.Error,
  },
  show: (id: string) => ({
    id,
    include: [
      'attachments.blob',
      'attachments.extractDataAIJob',
      'group',
      'patient',
      'referredBy',
      'summaryJobForCurrentAttachments',
      'patientDemographicsJob',
      'criteriaEvaluationJob.aiJobCriteria',
    ].join(','),
  }),
};

type CreateAdmissionInput = {
  groupId?: string;
  attachments: Upload[];
};

export async function createAdmission({ groupId, attachments }: CreateAdmissionInput) {
  return http
    .post('/reviews', { type: ReviewType.ADMISSION, groupId, attachments })
    .then((response) => ReviewModelFactory.get(response.data));
}

export async function indexAdmissions({ dateRange, ...params }: AdmissionsIndexParams, signal: GenericAbortSignal) {
  const filters = {
    ...params.filters,
    ...(dateRange ? { createdAt: [dateRange.startDate, dateRange.endDate] } : {}),
  } as ReviewsIndexParams['filters'];
  return indexReviews({ ...params, filters, type: 'admission' }, signal);
}

type UpdateAdmissionInput = {
  id: string;
  patient?: {
    name?: string;
    dateOfBirth?: string;
    sex?: string;
  };
  planType?: string;
  referralSource?: string;
};

export async function updateAdmission({ id, ...params }: UpdateAdmissionInput) {
  return http.put(`/reviews/${id}`, params).then((response) => ReviewModelFactory.get(response.data));
}

export async function deleteAdmission(id: string) {
  return http.delete(`/reviews/${id}`);
}
